// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";
import WhichEyeSection from "../card-ui/WhichEyeSection/WhichEyeSection";
import ExamCardToggles from "../card-ui/ExamCardToggles/ExamCardToggles";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import { DEFAULT_COLOR_VISION_CARD } from "src/constants/enums";

type ColorVisionCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
  onChangeEye: (value: string) => void;
  onChangeSectionOrder: (isRightFirst: boolean) => void;
};

const ColorVisionCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onChangeEye,
  onChangeSectionOrder,
  onRemoveCard,
}: ColorVisionCardType) => {
  const { t } = useTranslation();

  const examCardInfo = selectedExamCard ?? DEFAULT_COLOR_VISION_CARD;

  const { examType, eyeCheckboxes, sectionOrder, optionalToggles, binocular } =
    examCardInfo;

  const colorVisionTypes = useMemo(
    () => [
      { type: "ISHIHARA_12_PLATE", label: t("Ishihara 12-Plate") },
      { type: "ISHIHARA_24_PLATE", label: t("Ishihara 24-Plate") },
      { type: "D_15", label: t("Saturated D-15") },
      { type: "DESATURATED_D_15", label: t("Desaturated D-15") },
    ],
    [t]
  );

  const colorVisionEyeOptions = useMemo(
    () => [
      { value: false, label: t("Monocular") },
      { value: true, label: t("Binocular") },
    ],
    [t]
  );

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  // A button only needs to wrap to a new line once its own label can't fit
  // -- not at some arbitrary shared width. "ch" scales with character
  // count, so each button's floor tracks its own text; +2ch covers the
  // button's horizontal padding/border.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  return (
    <ExamCardContainer>
      <ExamCardHeader label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
          }}
        >
          {/* Type */}
          <Grid item display="flex" flexDirection="column" gap="4px">
            <TitleWithTooltip
              title={t("Type")}
              tooltip={t("More available soon.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {colorVisionTypes.map((colorVisionType) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(colorVisionType.label)}
                  key={colorVisionType.type}
                >
                  <ExamCardButton
                    isSelected={examType === colorVisionType.type}
                    updateValue={() =>
                      updateExamCardProperty("examType", colorVisionType.type)
                    }
                    label={colorVisionType.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Eye */}
          <Grid item display="flex" flexDirection="column" gap="4px">
            <TitleWithTooltip
              title={t("Eye")}
              tooltip={t(
                "Choose the binocular option to test both eyes at once or the monocular option to test one eye at a time."
              )}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {colorVisionEyeOptions.map((option) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(option.label)}
                  key={String(option.value)}
                >
                  <ExamCardButton
                    isSelected={option.value ? Boolean(binocular) : !binocular}
                    updateValue={() =>
                      updateExamCardProperty("binocular", option.value)
                    }
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <WhichEyeSection
            isRightFirst={sectionOrder?.right === 0}
            eyeCheckboxes={eyeCheckboxes}
            onChangeSectionOrder={onChangeSectionOrder}
            onChangeEye={onChangeEye}
            isHidden={Boolean(binocular)}
          />

          <Divider color="#5B6D7A" />

          <ExamCardToggles
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={DEFAULT_COLOR_VISION_CARD.optionalToggles}
          />
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default ColorVisionCard;
